/**
 * SlideDetailPanel.tsx
 *
 * Center-right detail panel for the selected slide in the pseudo-PPTX editor
 * (Build Bar — Step 2: Slides).
 *
 * Shows:
 *  1. Large SlideMockPreview (~400px wide, 16:9) — live-updates as fields change.
 *  2. Component-type dropdown — grouped by SLIDE_COMPONENT_FAMILIES.
 *  3. Section text input.
 *  4. Title + Subtitle text inputs.
 *  5. Blocks section — varies by component type:
 *       - Content components → bullet list with add/remove rows.
 *       - hero_stats         → 4 value/label input pairs.
 *       - data_timeline      → list of time + description pairs.
 *       - Others             → raw JSON textarea fallback.
 *  6. "Delete slide" danger button.
 *
 * All changes call `onUpdate(slideIndex, { field: value })` immediately.
 * The parent is responsible for debounced auto-save.
 */

import { useCallback } from 'react';

import {
  SLIDE_COMPONENTS,
  SLIDE_COMPONENT_FAMILIES,
  getComponentLabel,
  type SlideComponentType,
} from '@/constants/slideComponents';
import type { SlideConfig, SlideBlock } from '@/types/wizardV2';
import type { PlantillaBackground } from '@/types/wizard';
import { SlideMockPreview } from './SlideMockPreview';
import styles from './SlideDetailPanel.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SlideDetailPanelProps {
  /** The slide being edited. */
  slide: SlideConfig;
  /** Index of this slide in the wizard store's `slides` array. */
  slideIndex: number;
  /** Total number of slides (for nav counter display). */
  totalSlides: number;
  /** Plantilla preview image URL used as the slide canvas background. */
  plantillaBgUrl?: string;
  /** All available plantilla backgrounds for the bg picker. */
  availableBackgrounds?: PlantillaBackground[];
  /**
   * Called on every field change with a partial SlideConfig update.
   * Parent debounces auto-save calls; this fires immediately.
   */
  onUpdate: (index: number, partial: Partial<SlideConfig>) => void;
  /** Called when the user confirms removal of this slide. */
  onRemove: (index: number) => void;
  /** Navigate to the previous slide. */
  onPrev?: () => void;
  /** Navigate to the next slide. */
  onNext?: () => void;
  /** Whether there is a previous slide to navigate to. */
  hasPrev?: boolean;
  /** Whether there is a next slide to navigate to. */
  hasNext?: boolean;
}

// ─── Helper: build grouped <optgroup> options ─────────────────────────────────

/**
 * Build ordered groups of component-type options for the <select> element.
 * Families appear in the order they are declared in SLIDE_COMPONENT_FAMILIES.
 */
function buildComponentOptions(): { family: string; label: string; options: { value: string; label: string }[] }[] {
  const groups: Record<string, { value: string; label: string }[]> = {};

  for (const [key, meta] of Object.entries(SLIDE_COMPONENTS)) {
    const fam = meta.family;
    if (!groups[fam]) groups[fam] = [];
    groups[fam].push({ value: key, label: meta.label });
  }

  return Object.entries(SLIDE_COMPONENT_FAMILIES).map(([fam, familyLabel]) => ({
    family: fam,
    label: familyLabel,
    options: groups[fam] ?? [],
  }));
}

const COMPONENT_GROUPS = buildComponentOptions();

// ─── Helper: detect block-rendering strategy for a component type ─────────────

type BlocksStrategy = 'bullets' | 'stats' | 'timeline' | 'json';

/**
 * Determine how to render the Blocks section for a given component type.
 *
 * @param component - SlideConfig.component string.
 * @returns Strategy string consumed by the inline renderer.
 */
function resolveBlocksStrategy(component: string): BlocksStrategy {
  if (component === 'hero_stats') return 'stats';
  if (component === 'data_timeline') return 'timeline';
  const contentComponents: SlideComponentType[] = [
    'concepto_full',
    'content_image_right',
    'content_image_left',
    'journey_overview',
    'people_speaker_bio',
  ];
  if (contentComponents.includes(component as SlideComponentType)) return 'bullets';
  return 'json';
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * SlideDetailPanel — editable detail view for the focused slide.
 *
 * All field updates propagate immediately via `onUpdate`. The parent owns
 * the debounce + auto-save cycle so this component stays pure and lean.
 */
export const SlideDetailPanel = ({
  slide,
  slideIndex,
  totalSlides,
  plantillaBgUrl,
  availableBackgrounds,
  onUpdate,
  onRemove,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
}: SlideDetailPanelProps) => {
  // ── Field change handlers ──────────────────────────────────────────────────

  /** Generic string field update. */
  const handleField = useCallback(
    (field: keyof SlideConfig, value: string) => {
      onUpdate(slideIndex, { [field]: value } as Partial<SlideConfig>);
    },
    [slideIndex, onUpdate],
  );

  /** Replace the entire blocks array. */
  const handleBlocksChange = useCallback(
    (blocks: SlideBlock[]) => {
      onUpdate(slideIndex, { blocks });
    },
    [slideIndex, onUpdate],
  );

  // ── Bullet list handlers ───────────────────────────────────────────────────

  /**
   * Update the text of a single bullet block (type === "bullet" or any text block).
   */
  const handleBulletChange = (blockIndex: number, text: string) => {
    const next = (slide.blocks ?? []).map((b, i) => (i === blockIndex ? { ...b, text } : b));
    handleBlocksChange(next);
  };

  const handleBulletAdd = () => {
    handleBlocksChange([...(slide.blocks ?? []), { type: 'bullet', text: '' }]);
  };

  const handleBulletRemove = (blockIndex: number) => {
    handleBlocksChange((slide.blocks ?? []).filter((_, i) => i !== blockIndex));
  };

  // ── Stats handlers (hero_stats: 4 value/label pairs) ──────────────────────

  /**
   * The stats block structure expected:
   *  blocks = [{ type: 'stat', value: '500', label: 'Guests' }, ...]
   * We normalise missing blocks to empty defaults.
   */
  const safeBlocks = slide.blocks ?? [];
  const statBlocks = Array.from({ length: 4 }, (_, i) => ({
    ...(safeBlocks[i] ?? {}),
    type: 'stat',
    value: (safeBlocks[i] as { value?: string } | undefined)?.value ?? '',
    label: (safeBlocks[i] as { label?: string } | undefined)?.label ?? '',
  }));

  const handleStatChange = (statIndex: number, field: 'value' | 'label', text: string) => {
    const next = [...statBlocks];
    next[statIndex] = { ...next[statIndex], [field]: text };
    handleBlocksChange(next as SlideBlock[]);
  };

  // ── Timeline handlers (data_timeline: time + description pairs) ────────────

  /**
   * Timeline block structure:
   *  blocks = [{ type: 'timeline_item', time: '09:00', description: 'Registro' }, ...]
   */
  const handleTimelineChange = (
    itemIndex: number,
    field: 'time' | 'description',
    text: string,
  ) => {
    const next = (slide.blocks ?? []).map((b, i) =>
      i === itemIndex ? { ...b, [field]: text } : b,
    );
    handleBlocksChange(next);
  };

  const handleTimelineAdd = () => {
    handleBlocksChange([...(slide.blocks ?? []), { type: 'timeline_item', time: '', description: '' }]);
  };

  const handleTimelineRemove = (itemIndex: number) => {
    handleBlocksChange((slide.blocks ?? []).filter((_, i) => i !== itemIndex));
  };

  // ── Derived values ─────────────────────────────────────────────────────────

  const blocks = slide.blocks ?? [];
  const strategy = resolveBlocksStrategy(slide.component);
  const componentGroups = COMPONENT_GROUPS;
  const slideLabel = getComponentLabel(slide.component);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={styles.container}>
      {/* ── Navigation + Preview + Background picker row ──────────────── */}
      <div className={styles.navRow}>
        <button
          type="button"
          className={styles.navArrow}
          disabled={!hasPrev}
          onClick={onPrev}
          aria-label="Previous slide"
        >
          ‹
        </button>

        <div className={styles.previewCol}>
          <div className={styles.previewWrapper}>
            <SlideMockPreview
              component={slide.component}
              plantillaBgUrl={plantillaBgUrl}
              title={slide.title}
              section={slide.section}
              slideNumber={slideIndex + 1}
            />
          </div>

          <p className={styles.slideCounter}>
            Slide {slideIndex + 1} / {totalSlides}
          </p>

          {/* Background image picker */}
          {availableBackgrounds && availableBackgrounds.length > 1 && (
            <div className={styles.bgPicker}>
              <h4 className={styles.bgPickerTitle}>Background</h4>
              <div className={styles.bgGrid}>
                {availableBackgrounds.map((bg) => {
                  const currentIdx = slide.background?.plantilla_index ?? 0;
                  const isSelected = bg.index === currentIdx;
                  return (
                    <button
                      key={bg.index}
                      type="button"
                      className={`${styles.bgThumb} ${isSelected ? styles.bgThumbActive : ''}`}
                      onClick={() =>
                        onUpdate(slideIndex, {
                          background: { type: 'plantilla', plantilla_index: bg.index },
                        })
                      }
                      aria-label={`Background ${bg.index + 1}`}
                      title={`Background ${bg.index + 1} (${bg.tone})`}
                    >
                      <img src={bg.url} alt="" className={styles.bgThumbImg} loading="lazy" />
                      {isSelected && <span className={styles.bgThumbCheck}>✓</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <button
          type="button"
          className={styles.navArrow}
          disabled={!hasNext}
          onClick={onNext}
          aria-label="Next slide"
        >
          ›
        </button>
      </div>

      {/* ── Component type + Section (side by side) ─────────────────────── */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Component</h3>
        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor={`comp-${slideIndex}`}>
              Slide type
            </label>
            <select
              id={`comp-${slideIndex}`}
              className={styles.select}
              value={slide.component}
              onChange={(e) => handleField('component', e.target.value)}
              aria-label="Slide component type"
            >
              {componentGroups.map((group) => (
                <optgroup key={group.family} label={group.label}>
                  {group.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </optgroup>
              ))}
              {!SLIDE_COMPONENTS[slide.component as SlideComponentType] && (
                <option value={slide.component}>{slideLabel}</option>
              )}
            </select>
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor={`section-${slideIndex}`}>
              Section
            </label>
            <input
              id={`section-${slideIndex}`}
              className={styles.input}
              type="text"
              value={slide.section}
              onChange={(e) => handleField('section', e.target.value)}
              placeholder="E.g. Value Proposition"
              aria-label="Slide section"
            />
          </div>
        </div>
      </div>

      {/* ── Title + Subtitle (side by side) ────────────────────────────── */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Content</h3>
        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor={`title-${slideIndex}`}>
              Title
            </label>
            <input
              id={`title-${slideIndex}`}
              className={styles.input}
              type="text"
              value={slide.title}
              onChange={(e) => handleField('title', e.target.value)}
              placeholder="Slide title"
              aria-label="Slide title"
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor={`subtitle-${slideIndex}`}>
              Subtitle
            </label>
            <input
              id={`subtitle-${slideIndex}`}
              className={styles.input}
              type="text"
              value={slide.subtitle ?? ''}
              onChange={(e) => handleField('subtitle', e.target.value)}
              placeholder="Optional subtitle"
              aria-label="Slide subtitle"
            />
          </div>
        </div>
      </div>

      {/* ── Blocks section — strategy-based rendering ────────────────────── */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Blocks</h3>

        {/* ── Bullets strategy ──────────────────────────────────────────── */}
        {strategy === 'bullets' && (
          <div className={styles.blocksList}>
            {blocks.map((block, bi) => (
              <div key={bi} className={styles.blockRow}>
                <input
                  className={styles.blockInput}
                  type="text"
                  value={typeof block.text === 'string' ? block.text : ''}
                  onChange={(e) => handleBulletChange(bi, e.target.value)}
                  placeholder={`Bullet ${bi + 1}`}
                  aria-label={`Bullet ${bi + 1}`}
                />
                <button
                  type="button"
                  className={styles.blockRemoveBtn}
                  onClick={() => handleBulletRemove(bi)}
                  aria-label={`Remove bullet ${bi + 1}`}
                  title="Remove"
                >
                  &times;
                </button>
              </div>
            ))}
            <button
              type="button"
              className={styles.addBlockBtn}
              onClick={handleBulletAdd}
              aria-label="Add bullet"
            >
              + Add bullet
            </button>
          </div>
        )}

        {/* ── Stats strategy (hero_stats): 4 value/label pairs ──────────── */}
        {strategy === 'stats' && (
          <div className={styles.statGrid}>
            {statBlocks.map((stat, si) => (
              <div key={si} className={styles.statPair}>
                <span className={styles.statPairLabel}>Stat {si + 1}</span>
                <input
                  className={styles.blockInput}
                  type="text"
                  value={typeof stat.value === 'string' ? stat.value : ''}
                  onChange={(e) => handleStatChange(si, 'value', e.target.value)}
                  placeholder="Value (e.g. 500)"
                  aria-label={`Stat ${si + 1} value`}
                />
                <input
                  className={styles.blockInput}
                  type="text"
                  value={typeof stat.label === 'string' ? stat.label : ''}
                  onChange={(e) => handleStatChange(si, 'label', e.target.value)}
                  placeholder="Label (e.g. Guests)"
                  aria-label={`Stat ${si + 1} label`}
                />
              </div>
            ))}
          </div>
        )}

        {/* ── Timeline strategy (data_timeline): time + description ──────── */}
        {strategy === 'timeline' && (
          <div className={styles.blocksList}>
            {blocks.map((item, ti) => (
              <div key={ti} className={styles.timelinePair}>
                <input
                  className={styles.timeInput}
                  type="text"
                  value={typeof item.time === 'string' ? item.time : ''}
                  onChange={(e) => handleTimelineChange(ti, 'time', e.target.value)}
                  placeholder="09:00"
                  aria-label={`Time item ${ti + 1}`}
                />
                <input
                  className={styles.blockInput}
                  type="text"
                  value={typeof item.description === 'string' ? item.description : ''}
                  onChange={(e) => handleTimelineChange(ti, 'description', e.target.value)}
                  placeholder="Activity"
                  aria-label={`Description item ${ti + 1}`}
                />
                <button
                  type="button"
                  className={styles.blockRemoveBtn}
                  onClick={() => handleTimelineRemove(ti)}
                  aria-label={`Remove item ${ti + 1}`}
                  title="Remove"
                >
                  &times;
                </button>
              </div>
            ))}
            <button
              type="button"
              className={styles.addBlockBtn}
              onClick={handleTimelineAdd}
              aria-label="Add timeline item"
            >
              + Add item
            </button>
          </div>
        )}

        {/* ── Key-value block editor (replaces raw JSON) ─────────────────── */}
        {strategy === 'json' && (
          <div className={styles.blocksList}>
            {blocks.map((block, bi) => {
              const entries = Object.entries(block).filter(([k]) => k !== 'type');
              return (
                <div key={bi} className={styles.kvBlock}>
                  <div className={styles.kvBlockHeader}>
                    <span className={styles.kvBlockType}>
                      {typeof block.type === 'string' ? block.type : `Block ${bi + 1}`}
                    </span>
                    <button
                      type="button"
                      className={styles.blockRemoveBtn}
                      onClick={() => handleBlocksChange(blocks.filter((_, i) => i !== bi))}
                      aria-label={`Remove block ${bi + 1}`}
                      title="Remove"
                    >
                      &times;
                    </button>
                  </div>
                  {entries.map(([key, val]) => (
                    <div key={key} className={styles.kvRow}>
                      <span className={styles.kvKey}>{key}</span>
                      {typeof val === 'string' ? (
                        <input
                          className={styles.blockInput}
                          type="text"
                          value={val}
                          onChange={(e) => {
                            const next = blocks.map((b, i) =>
                              i === bi ? { ...b, [key]: e.target.value } : b,
                            );
                            handleBlocksChange(next);
                          }}
                          placeholder={key}
                          aria-label={`${key} of block ${bi + 1}`}
                        />
                      ) : Array.isArray(val) ? (
                        <span className={styles.blockInput} style={{ opacity: 0.5, cursor: 'default' }}>
                          [{val.length} items]
                        </span>
                      ) : (
                        <input
                          className={styles.blockInput}
                          type="text"
                          value={String(val ?? '')}
                          onChange={(e) => {
                            const next = blocks.map((b, i) =>
                              i === bi ? { ...b, [key]: e.target.value } : b,
                            );
                            handleBlocksChange(next);
                          }}
                          placeholder={key}
                          aria-label={`${key} of block ${bi + 1}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
            <button
              type="button"
              className={styles.addBlockBtn}
              onClick={() => handleBlocksChange([...blocks, { type: 'text', text: '' }])}
              aria-label="Add block"
            >
              + Add block
            </button>
          </div>
        )}
      </div>

      {/* ── Danger zone ─────────────────────────────────────────────────── */}
      <div className={styles.dangerZone}>
        <button
          type="button"
          className={styles.removeBtn}
          onClick={() => onRemove(slideIndex)}
          aria-label="Delete this slide"
        >
          Delete slide
        </button>
      </div>
    </div>
  );
};
